import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ContactCreateEditRequest, IContact } from "../../models/Contact";
import useContactsMutations from "../../data/useContactsMutations";
import { TextInput } from "../shared/Inputs/TextInput";
import PickAddress from "../shared/Addresses/PickAddress";
import { SearchContact } from "./SearchContact";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useNotification from "../notifications/useNotifications";
import FileUpload from "../shared/Inputs/FileUpload";
import LoadingWheel from "../shared/LoadingWheel";
import { DocumentType, IDocument } from "../../models/Document";
import SelectInput from "../shared/Inputs/SelectInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import { AccountType } from "../../models/account/IAccount";

interface ContactSelectorProps {
  onContactSelected: (contact: IContact | null) => void;
  initialContact?: IContact | null;
}

export default function ContactSelector({ onContactSelected, initialContact = null }: ContactSelectorProps) {
  const { addNotification } = useNotification();
  const { create: createContact, uploadDoc } = useContactsMutations();
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [selectedContact, setSelectedContact] = useState<IContact | null>(initialContact);
  const [toUpload, setToUpload] = useState<any>([]);
  const [uploadIsLoading, setUploadIsLoading] = useState(false);

  const updateToUploadFiles = (files: any) => setToUpload({ ...files });

  const { register, handleSubmit, setValue, getValues, watch, reset, } = useForm<ContactCreateEditRequest>({
    defaultValues: {
      fullname: "",
      email: "",
      telephone1: "",
      telephone2: "",
      line1: "",
      line2: "",
      line3: "",
      line4: "",
      locality: "",
      town: "",
      county: "",
      postcode: "",
      contactType: 0,
      notes: "",
      accountName: "",
      bankAccountName: "",
      bankAccountNumber: "",
      bankSortCode: "",
      createAccount: false,
    },
  });

  const handleCreateContact = (data: ContactCreateEditRequest) => {

    if (!data.fullname || data.fullname.trim() === "") {
      addNotification({
        variant: "error",
        primaryText: "Please add at least a name before creating a contact.",
      });
      return;
    }

    setUploadIsLoading(true);
    data.createAccountQuery = true; // Always create an account when creating a contact here
    data.accountType = data.createAccount ? AccountType.Manual : AccountType.Auto;

    createContact.mutate(data, {
      onSuccess: (res: any) => {
        setSelectedContact(res.data);
        onContactSelected(res.data);

        handleUploadDocs(res.data.id);
      },
      onError: (error) => {
        console.log(error);
        setUploadIsLoading(false);
      },
    });
  };

  const handleUploadDocs = (contactId: string) => {
    //attach files to http request
    setUploadIsLoading(true);

    const formData = new FormData();

    for (let i = 0; i < Object.keys(toUpload).length; i++) {
      formData.append(DocumentType[DocumentType.Identity].toString(), toUpload[i]);
    }

    if (Object.keys(toUpload).length === 0) {
      setIsCreatingContact(false);
      setUploadIsLoading(false);
      return;
    }

    uploadDoc.mutate({ data: formData, contactId: contactId }, {
      onSuccess: () => {
        setToUpload([]);
      },
      onError: (error) => {
        console.log(error);
      },
      onSettled: () => {
        setIsCreatingContact(false);
        setUploadIsLoading(false);
      }
    })
  }

  useEffect(() => {
    // console.log("selectedContact changed", selectedContact);
    if (!selectedContact || selectedContact === null) {

      reset({
        fullname: "",
        email: "",
        telephone1: "",
        telephone2: "",
        line1: "",
        line2: "",
        line3: "",
        line4: "",
        locality: "",
        town: "",
        county: "",
        postcode: "",
        contactType: 0,
        notes: "",
        accountName: "",
        bankAccountName: "",
        bankAccountNumber: "",
        bankSortCode: "",
        createAccount: false,
      });

    } else {
      reset({ ...selectedContact });
    }
  }, [selectedContact]);

  const watchFullName = watch("fullname");
  const watchCreateAccount = watch("createAccount");

  useEffect(() => {
    setValue("accountName", watchFullName ? watchFullName : "");
  }, [watchFullName]);

  return (
    <div>
      {!isCreatingContact ? (
        <>
          <SearchContact label="Select Contact"
            selectedContact={selectedContact}
            onContactSelect={(contact) => {
              setSelectedContact(contact);
              onContactSelected(contact);
            }} />

          {selectedContact && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Full Name"
                register={register}
                registerName="fullname"
                disabled={true}
              />
              <TextInput
                label="Email"
                register={register}
                registerName="email"
                disabled={true}
              />
              <TextInput
                label="Telephone 1"
                register={register}
                registerName="telephone1"
                disabled={true}
              />
              <TextInput
                label="Telephone 2"
                register={register}
                registerName="telephone2"
                disabled={true}
              />
              <TextInput
                label="Postcode"
                register={register}
                registerName="postcode"
                disabled={true}
              />
              <TextInput
                label="Address Line 1"
                register={register}
                registerName="line1"
                disabled={true}
              />
            </div>
          )}

          <div className="mt-2 flex gap-2">
            <ButtonInput
              label="Create Contact"
              isSubmit={false}
              onClick={() => {
                setIsCreatingContact(true);
                setSelectedContact(null);
                onContactSelected(null);
              }}
              classes=""
            />
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              label="Full Name"
              register={register}
              registerName="fullname"
              required
            />
            <TextInput
              label="Email"
              register={register}
              registerName="email"
            />
            <TextInput
              label="Telephone 1"
              register={register}
              registerName="telephone1"
            />
            <TextInput
              label="Telephone 2"
              register={register}
              registerName="telephone2"
            />
          </div>

          <PickAddress
            register={register}
            setValue={setValue}
            watch={watch}
          />

          <div className="flex flex-col gap-4">
            <h1 className="text-lg font-semibold col-span-1 md:col-span-3">Account Details</h1>

            <CheckboxInput
              label={"Create Regulars Account?"}
              register={register}
              registerName="createAccount"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {watchCreateAccount ? (
                <TextInput
                  label="Account Name"
                  register={register}
                  registerName="accountName"
                />
              ) : <div />}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">

            <h1 className="text-lg font-semibold col-span-1 md:col-span-3">Payment Details</h1>
            <TextInput
              label="Bank Account Name"
              register={register}
              registerName="bankAccountName"
            />
            <TextInput
              label="Bank Account Number"
              register={register}
              registerName="bankAccountNumber"
            />
            <TextInput
              label="Bank Sort Code"
              register={register}
              registerName="bankSortCode"
            />
          </div>

          <div>
            {/* ID docs */}
            {uploadIsLoading ? (
              <div className="flex items-center py-[10vh]">
                <LoadingWheel />
              </div>
            ) : (
              <FileUpload
                accept=".jpg,.png,.jpeg,.pdf"
                label="Upload ID Documents"
                desc="Optionally upload identity documents for this contact. You can upload multiple files."
                multiple
                updateFilesCb={updateToUploadFiles}
              />
            )}
          </div>

          <div className="flex gap-2">
            <ButtonInput
              label="Save"
              isSubmit={false}
              onClick={handleSubmit(handleCreateContact)}
              classes=""
              isLoading={createContact.isLoading}
            />
            <ButtonInput
              label="Cancel"
              isSubmit={false}
              onClick={() => setIsCreatingContact(false)}
              classes=""
            />
          </div>
        </div>
      )}
    </div>
  );
} 